/*
  Usage:
  1. Set your Storefront API token and shop domain below.
  2. Call fetchAllProductVariants('product-handle') to get all variants for a product.
  3. The output matches the JSON structure expected by common.js.
  4. You can inject this JSON into your theme as a <script type="application/json" data-name="variant-json"> block.
*/

const STOREFRONT_ACCESS_TOKEN = '09a844487f3fa6b64f3300f8b5bf8067'; // <-- Set this
const SHOP_DOMAIN = 'curvy-com.myshopify.com'; // <-- Set this
const GRAPHQL_ENDPOINT = `https://${SHOP_DOMAIN}/api/2024-04/graphql.json`;

async function fetchAllProductVariants(productHandle) {
  let hasNextPage = true;
  let endCursor = null;
  let allVariants = [];
  let productTitle = '';
  let productOptions = [];
  let product = null;

  while (hasNextPage) {
    const query = `
      query getProductVariants($handle: String!, $after: String) {
        product(handle: $handle) {
          title
          options {
            name
            values
          }
          variants(first: 250, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              id
              availableForSale
              compareAtPriceV2 { amount }
              image {
                id
                url
                altText
                width
                height
              }
              priceV2 { amount }
              quantityAvailable
              sku
              title
              selectedOptions { name value }
              unitPrice { amount }
              unitPriceMeasurement { referenceUnit referenceValue }
            }
          }
        }
      }
    `;
    const variables = { handle: productHandle, after: endCursor };
    
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('GraphQL request failed:', errorText);
      throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }
    
    if (!result.data || !result.data.product) {
      console.error('No product data found:', result);
      throw new Error('Product not found');
    }
    product = result.data.product;
    productTitle = product.title;
    productOptions = product.options;
    const variants = product.variants.nodes;
    allVariants = allVariants.concat(variants);
    hasNextPage = product.variants.pageInfo.hasNextPage;
    endCursor = product.variants.pageInfo.endCursor;
  }
  return { productTitle, productOptions, variants: allVariants };
}

function transformVariants(productTitle, variants) {
  return variants.map(variant => {
    const option1 = variant.selectedOptions[0]?.value || null;
    const option2 = variant.selectedOptions[1]?.value || null;
    const option3 = variant.selectedOptions[2]?.value || null;
    return {
      available: variant.availableForSale,
      compare_at_price: variant.compareAtPriceV2 ? variant.compareAtPriceV2.amount : null,
      featured_media: variant.image ? {
        id: variant.image.id,
        url: variant.image.url,
        alt: variant.image.altText,
        width: variant.image.width,
        height: variant.image.height,
      } : null,
      featured_image: variant.image ? variant.image.id : null,
      id: variant.id.replace('gid://shopify/ProductVariant/', ''),
      inventory_management: null, // Not available in Storefront API
      inventory_policy: null,     // Not available in Storefront API
      inventory_quantity: variant.quantityAvailable,
      name: productTitle,
      option1,
      option2,
      option3,
      options: [option1, option2, option3].filter(Boolean),
      price: variant.priceV2.amount,
      sku: variant.sku,
      unit_price_measurement: !!variant.unitPrice,
      unit_price: variant.unitPrice ? variant.unitPrice.amount : null,
      reference_value: variant.unitPriceMeasurement ? variant.unitPriceMeasurement.referenceValue : null,
      reference_unit: variant.unitPriceMeasurement ? variant.unitPriceMeasurement.referenceUnit : null,
      title: variant.title,
      store_availabilities: null, // Not available in Storefront API
      selling_plans: {}, // Not implemented here
    };
  });
}

(async () => {
  try {
    const previousPlace = document.querySelector('[type="application/json"][data-name="variant-json"][data-handle="' + window.productHandle + '"]');

    if (previousPlace) {
      const previousData = JSON.parse(previousPlace.textContent);
      if (previousData.length < 249) {
        return;
      }
    }

    const { productTitle, productOptions, variants } = await fetchAllProductVariants(window.productHandle);
    const variantJson = transformVariants(productTitle, variants);

    previousPlace.textContent = JSON.stringify(variantJson);

  } catch (error) {
    console.error('Failed to fetch product variants:', error);
  }
})();